/**
 * Vollebak — grey out sold-out sizes in the collection size filter on first load.
 *
 * The size facet only marks a value disabled when its in-stock count is 0, but
 * Shopify only computes in-stock counts when filter.v.availability=1 is applied.
 * We deliberately do NOT apply availability to the GRID when no size is selected
 * (the grid must keep showing sold-out / pre-order / prototype products). So
 * instead, when no size filter is active, we make a BACKGROUND section request
 * with availability=1, read which size values come back disabled, and disable
 * those same inputs in the live (unfiltered) facet UI. The grid and the URL are
 * never touched — only the size checkboxes are greyed (existing CSS keys off
 * `.checkbox__input:disabled`).
 *
 * When a size filter IS active, facets.js already injects availability=1, so the
 * native disabled states are correct and this does nothing.
 */
(() => {
  const SIZE = 'filter.v.option.size';

  const sectionEl = document.querySelector('[section-id]');
  if (!sectionEl) return;
  const sectionId = sectionEl.getAttribute('section-id');
  const sectionWrapper = sectionEl.closest('.shopify-section') || document.body;

  const sizeActive = () => new URLSearchParams(window.location.search).has(SIZE);
  const liveSizeInputs = () => document.querySelectorAll(`input[name="${SIZE}"]`);

  // Cache the disabled-size set per probe URL (depends on the non-size filters).
  const cache = new Map();
  async function disabledSizes() {
    const probe = new URL(window.location.href);
    probe.searchParams.set('filter.v.availability', '1');
    probe.searchParams.set('section_id', sectionId);
    const key = probe.toString();
    if (cache.has(key)) return cache.get(key);

    const set = new Set();
    try {
      const html = await fetch(key).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll(`input[name="${SIZE}"]`).forEach((i) => {
        if (i.disabled) set.add(i.value);
      });
    } catch (e) {
      /* network/parse failure — leave sizes as-is, non-fatal */
    }
    cache.set(key, set);
    return set;
  }

  let busy = false;
  async function apply() {
    if (busy || sizeActive()) return; // a size is selected -> native greying is already correct
    const inputs = liveSizeInputs();
    if (!inputs.length) return;
    busy = true;
    try {
      const disabled = await disabledSizes();
      liveSizeInputs().forEach((i) => {
        if (disabled.has(i.value)) i.disabled = true;
      });
    } finally {
      busy = false;
    }
  }

  if (document.readyState !== 'loading') apply();
  else document.addEventListener('DOMContentLoaded', () => apply(), { once: true });

  // Re-apply after the facets/section re-render (filters applied or cleared).
  // Setting `disabled` is an attribute change, not childList, so this never
  // re-triggers itself.
  let t;
  const observer = new MutationObserver(() => {
    clearTimeout(t);
    t = setTimeout(() => apply(), 60);
  });
  observer.observe(sectionWrapper, { childList: true, subtree: true });
})();
